import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmailDeliveryModal = ({ isOpen, invoice, onClose, onConfirm }) => {
  const [emailTemplate, setEmailTemplate] = useState('professional');
  const [recipientEmail, setRecipientEmail] = useState(invoice?.customerEmail || '');
  const [ccEmails, setCcEmails] = useState('');
  const [subject, setSubject] = useState(`Invoice ${invoice?.invoiceNumber} from Business Jadhak`);
  const [message, setMessage] = useState(`Dear ${invoice?.customerName},\n\nPlease find attached your invoice for ${invoice?.plan} Plan.\n\nThank you for your business!`);
  const [trackDelivery, setTrackDelivery] = useState(true);

  if (!isOpen || !invoice) return null;

  const handleSend = () => {
    onConfirm({
      invoice,
      emailTemplate,
      recipientEmail,
      ccEmails,
      subject,
      message,
      trackDelivery
    });
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl bg-card rounded-xl elevation-2xl overflow-hidden border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="Mail" size={24} className="text-primary" />
              Send Invoice via Email
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-smooth"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Invoice Info */}
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Number</p>
                  <p className="text-base font-mono font-semibold text-foreground">{invoice?.invoiceNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="text-base font-semibold text-foreground">₹{invoice?.amount?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Email Template */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Template
              </label>
              <select
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="professional">Professional Invoice Template</option>
                <option value="simple">Simple Invoice Template</option>
                <option value="detailed">Detailed Invoice Template</option>
              </select>
            </div>

            {/* Recipient Email */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Recipient Email *
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* CC Emails */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CC (Optional)
              </label>
              <input
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e?.target?.value)}
                placeholder="email1@example.com, email2@example.com"
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e?.target?.value)}
                rows={6}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              />
            </div>

            {/* Delivery Tracking */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={trackDelivery}
                onChange={(e) => setTrackDelivery(e?.target?.checked)}
                className="w-4 h-4 rounded border-border"
              />
              <label className="text-sm text-foreground">
                Track email delivery and opens
              </label>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-info/10 border border-info/20 rounded-md">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-info flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-info font-body">
                    Email will include:
                  </p>
                  <ul className="text-xs text-muted-foreground font-caption space-y-1 ml-4 list-disc">
                    <li>PDF invoice attachment</li>
                    <li>Embedded payment link with QR code</li>
                    <li>Digital signature for authenticity</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={handleSend}
              disabled={!recipientEmail || !subject || !message}
              iconName="Send"
              iconPosition="left"
              fullWidth
            >
              Send Invoice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDeliveryModal;